<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { cn } from '@/lib/utils';
  import { getTypedAm5 } from '@/Util/XertAm5';
  import { parseStorage, saveStorage } from '@/Util/utils';
  import {
    MARKER_KIND_ORDER,
    MARKER_KINDS,
    SIGNATURE_LOAD_FIELDS,
    SIGNATURE_OVERLAYS,
    SIGNATURE_PANELS,
    SIGNATURE_TOGGLES,
  } from './seriesConfig';
  import { RACE_EVENT_COLORS } from '@/Common/activityTagOptions';
  import {
    buildBreakthroughs,
    markersToShow,
    isBreakthrough,
    buildDays,
    buildMarkers,
    chartTimeToDate,
    type ActivityMarker,
    type Breakthrough, type RaceEvent,
    type ChartDay,
    type ChartMarker,
    type MarkerKind,
  } from './chartData';
  import {
    addBreakthroughDiamonds,
    addDayTooltip,
    addEventsAxis,
    addMarkerLanes,
    addProgramStartGuide,
    addSelectionCursor,
    addTodayGuide,
    colinearScales,
    keepTooltipsOnScreen,
  } from './chartHelpers';
  import type { MeasureRow } from './progressionApi';

  // Needs amCharts loaded first: mount it inside <Am5Loader>.
  const { am5, am5xy, am5themes_Animated } = getTypedAm5();
  am5.addLicense('AM5C87942260');

  export let rows: MeasureRow[] | null = null;
  /** The athlete's ir_params; where present, each signature scale and its load scale are drawn colinear. */
  export let irParams: any = null;
  export let breakthroughs: Breakthrough[] | null = null;
  export let raceEvents: RaceEvent[] | null = null;
  /** Flagged activities and ones whose signature or training load was set by hand. */
  export let markers: ActivityMarker[] | null = null;
  /** The date range to draw, as `YYYY-MM-DD` in the athlete's timezone. `from` is empty for "all". */
  export let from: string = '';
  export let to: string = '';
  export let today: string = '';
  /** Where an XFAI plan starts (or will start), `YYYY-MM-DD` in the athlete's timezone; '' when there is none. */
  export let programStart: string = '';
  export let hideFuture: boolean = false;

  const dispatch = createEventDispatcher<{ select: { from: string; to: string } }>();
  /** Dragging across any of the charts picks out a stretch of days, which the page turns into the date range. */
  const selected = (from: number, to: number) => dispatch('select', { from: chartTimeToDate(from), to: chartTimeToDate(to) });

  // Height of the strip the flagged / manually set activities sit in, and how much of the rest each chart gets.
  const STRIP_PX = 64;
  // The strip the breakthrough diamonds sit in, above that one, as on the XPMC chart.
  const BREAKTHROUGH_STRIP_PX = 56;
  const PANEL_SHARE = [0.32, 0.32, 0.36];
  // Past this many markers the guidelines only appear for the one being pointed at.
  const GUIDELINES_ALWAYS_MAX = 300;
  const GUIDE_OPACITY = 0.2;
  // Height of each panel's title (top-left of its plot), which the Today / program-start labels start below.
  const TITLE_INSET = 30;

  let chartEl: HTMLDivElement;
  let mounted = false;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  let root: any = null;
  // Everything a toggle can switch, across the charts: id -> the series (one per chart).
  let toggleSeries: Record<string, any[]> = {};
  // The right-hand scales, which exist only while something on them is showing.
  let overlayAxes: { ids: string[]; axis: any }[] = [];
  // Lower Threshold widens the TP scale only while it's on, so its chip rescales the panels in place.
  let ltpRescales: (() => void)[] = [];
  // A guideline down each chart for each marker.
  let guides: { marker: ChartMarker; ranges: any[] }[] = [];

  const markerToggle = (kind: MarkerKind) => `mk:${kind}`;

  // Which toggles are on. One set for all the charts.
  // Kept in the session, so the charts open the same way after a reload.
  const defaultOn: Record<string, boolean> = {
    ...Object.fromEntries(SIGNATURE_TOGGLES.map((toggle) => [toggle.id, toggle.on])),
    raceEvents: true,
    ...Object.fromEntries(MARKER_KIND_ORDER.map((kind) => [markerToggle(kind), true])),
  };
  const savedLines = parseStorage('progressionCharts').signatureLines;
  let on: Record<string, boolean> = { ...defaultOn };
  if (savedLines && typeof savedLines === 'object') {
    for (const id of Object.keys(defaultOn)) if (typeof savedLines[id] === 'boolean') on[id] = savedLines[id];
  }

  // With "Hide future" off, a range that ends today (a preset) or is left open stays open to whatever
  // is scheduled after it. A range with its own past end date (a custom pick, or a drag on the chart)
  // stops exactly there -- picking a specific week shouldn't pull in a year of future activities.
  $: drawTo = !hideFuture && today && (to === today || to === '') ? '' : to;
  $: days = buildDays(rows, from, drawTo, today, hideFuture).map((day): ChartDay => ({
    ...day,
    hie_kj: typeof day.atc === 'number' ? day.atc / 1000 : undefined,
  }));
  $: allMarks = buildBreakthroughs(breakthroughs, from, drawTo, today, hideFuture, raceEvents);
  $: hasRaceEvents = allMarks.some((mark) => mark.kind);
  // The two chips decide what the strip draws (see markersToShow). Booleans of their own, so toggling other chips doesn't redraw it.
  $: breakthroughsOn = on.breakthroughs;
  $: raceEventsOn = on.raceEvents;
  $: marks = markersToShow(allMarks, breakthroughsOn, raceEventsOn);
  $: hasBreakthroughs = allMarks.some(isBreakthrough);
  $: chartMarkers = buildMarkers(markers, from, drawTo, today, hideFuture);
  $: markerKinds = MARKER_KIND_ORDER.filter((kind) => chartMarkers.some((marker) => marker.kinds.includes(kind)));
  $: if (mounted) rebuild(days, marks, chartMarkers);

  const formatValue = (value: number, decimals: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  function rebuild(chartDays: ChartDay[], chartMarks: ReturnType<typeof buildBreakthroughs>, activityMarkers: ChartMarker[]) {
    root?.dispose();
    root = null;
    toggleSeries = {};
    overlayAxes = [];
    ltpRescales = [];
    guides = [];
    if (!chartEl || !chartDays.length) return;

    // Tooltips may draw past the chart's own canvas: the marker/breakthrough strips sit at its very top edge, so a
    // tooltip there otherwise gets squeezed against (or cut off at) that edge instead of sitting above its marker.
    root = am5.Root.new(chartEl, { tooltipContainerBounds: { top: 240, right: 120, bottom: 120, left: 120 } });
    keepTooltipsOnScreen(root, chartEl);
    root.setThemes([am5themes_Animated.new(root)]);
    root.fontFamily = 'inherit';
    root.container.set('layout', root.verticalLayout);

    const narrow = chartEl.clientWidth < 640;
    const charts: any[] = [];

    // The time axes of the strip and all the charts cover the same days: half a day either side, as a
    // day's point is drawn around its midnight.
    const sharedRange = {
      min: chartDays[0].t - 12 * 3600 * 1000,
      max: chartDays[chartDays.length - 1].t + 12 * 3600 * 1000,
      strictMinMax: true,
    };

    const track = (id: string, series: any) => {
      (toggleSeries[id] ||= []).push(series);
    };

    // Two strips above the charts: the breakthroughs and the Race / Event targets (turned off together, they take their strip with them), then
    // the flagged / manually set activities.
    const showBreakthroughs = chartMarks.length > 0;
    const totalHeight = chartEl.clientHeight || 660;
    const pct = (px: number) => Math.min(30, (px / totalHeight) * 100);
    const breakthroughHeight = showBreakthroughs ? pct(BREAKTHROUGH_STRIP_PX) : 0;
    const markerHeight = activityMarkers.length ? pct(STRIP_PX) : 0;
    const stripHeight = breakthroughHeight + markerHeight;
    const makeStrip = (height: number) => {
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          height: am5.percent(height),
        })
      );
      chart.zoomOutButton.set('forceHidden', true);
      const renderer = am5xy.AxisRendererX.new(root, { minGridDistance: 70 });
      renderer.grid.template.setAll({ strokeOpacity: 0.08 });
      renderer.labels.template.set('forceHidden', true);
      const dateAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, { baseInterval: { timeUnit: 'day', count: 1 }, renderer, ...sharedRange })
      );
      return { chart, dateAxis };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const strips: { chart: any; dateAxis: any }[] = [];
    let breakthroughStrip: { chart: any; dateAxis: any } | null = null;
    let markerStrip: { chart: any; dateAxis: any } | null = null;
    if (breakthroughHeight) strips.push((breakthroughStrip = makeStrip(breakthroughHeight)));
    if (markerHeight) strips.push((markerStrip = makeStrip(markerHeight)));

    // Training load, recovery load and form each come as low, high and peak: every chart draws the part that goes
    // with it (low for threshold power, high for HIE, peak for peak power) under the one legend entry.
    const overlaysFor = (panelId: string) =>
      SIGNATURE_OVERLAYS.map((overlay) =>
        overlay.perPanel
          ? { ...overlay, field: SIGNATURE_LOAD_FIELDS[panelId][overlay.perPanel], label: `${SIGNATURE_LOAD_FIELDS[panelId].label} ${overlay.label}` }
          : overlay
      );
    // The range of some fields over the whole history: every scale is set to the values it carries, so nothing is clipped.
    const extentOf = (fields: string[]) => {
      let min = Infinity;
      let max = -Infinity;
      for (const day of chartDays) {
        for (const field of fields) {
          const value = day[field];
          if (typeof value === 'number' && isFinite(value)) {
            min = Math.min(min, value);
            max = Math.max(max, value);
          }
        }
      }
      return max > min ? { min, max } : null;
    };
    const dot = (color: string) => `<span style="color:${color}">●</span>`;
    const number = (value: unknown, decimals: number) => (typeof value === 'number' ? formatValue(value, decimals) : '');
    // The balloon: every line showing on any of the charts, wherever the pointer is. The signature lines are a list;
    // training load, recovery load and form are a small table of low / high / peak, one row for each that is on.
    const balloonHtml = (day: ChartDay): string => {
      const heading = new Date(day.t).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      const list = [...SIGNATURE_PANELS.flatMap((each) => each.lines), ...SIGNATURE_OVERLAYS.filter((overlay) => !overlay.perPanel)]
        .filter((entry) => (!(entry.id in on) || on[entry.id]) && typeof day[entry.field] === 'number')
        .map(
          (entry) =>
            `<div style="white-space:nowrap">${dot(entry.color)} ${entry.label}: <b>${number(day[entry.field], entry.decimals)}</b>${entry.unit ? ' ' + entry.unit : ''}</div>`
        )
        .join('');
      const categories = SIGNATURE_OVERLAYS.filter((overlay) => overlay.perPanel && on[overlay.id]);
      const table = categories.length
        ? `<table style="border-collapse:collapse;margin-top:4px;font-size:12px"><thead><tr><th></th>${SIGNATURE_PANELS.map(
            (each) => `<th style="font-weight:400;color:#6B7280;text-align:right;padding:0 0 0 10px">${SIGNATURE_LOAD_FIELDS[each.id].label}</th>`
          ).join('')}</tr></thead><tbody>${categories
            .map(
              (overlay) =>
                `<tr><td style="white-space:nowrap">${dot(overlay.color)} ${overlay.label}</td>${SIGNATURE_PANELS.map(
                  (each) =>
                    `<td style="text-align:right;font-weight:600;padding:0 0 0 10px">${number(day[SIGNATURE_LOAD_FIELDS[each.id][overlay.perPanel!]], overlay.decimals)}</td>`
                ).join('')}</tr>`
            )
            .join('')}</tbody></table>`
        : '';
      return `<div style="font-size:12px;line-height:1.45;color:#111827"><div style="font-weight:700;white-space:nowrap">${heading}</div>${list}${table}</div>`;
    };

    SIGNATURE_PANELS.forEach((panel, index) => {
      const last = index === SIGNATURE_PANELS.length - 1;
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          // The time axis stays where it is: dragging selects a range rather than scrolling.
          panX: false,
          panY: false,
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
          // The bottom chart also carries the date labels, so it gets a little more height.
          height: am5.percent((100 - stripHeight) * PANEL_SHARE[index]),
        })
      );
      // The time axis never zooms, so there is nothing to zoom back out of.
      chart.zoomOutButton.set('forceHidden', true);
      charts.push(chart);

      const dateRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 70 });
      dateRenderer.grid.template.setAll({ strokeOpacity: 0.08 });
      dateRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
      if (!last) dateRenderer.labels.template.set('forceHidden', true);
      const dateAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, { baseInterval: { timeUnit: 'day', count: 1 }, renderer: dateRenderer, ...sharedRange })
      );

      // Same width whatever the numbers, so the plots line up above one another.
      // minPosition/maxPosition drop a value that lands right on the panel's top or bottom edge, where it would print
      // over the neighbouring panel's own end value (e.g. "350" on top of "20").
      const labelStyle = { fontSize: narrow ? 10 : 11, fill: am5.color('#6B7280'), paddingLeft: 3, paddingRight: 3, minPosition: 0.05, maxPosition: 0.95 };
      const yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.grid.template.setAll({ strokeOpacity: 0.08 });
      yRenderer.labels.template.setAll(labelStyle);
      const panelOverlays = overlaysFor(panel.id);
      // The signature is p0 + k1 * its load, so starting its scale at p0 and rising k1 per unit of load puts it on the
      // same line as the load scale (the load scale gets the matching range below).
      const loadRange = extentOf(panelOverlays.filter((each) => each.axis === 'load').map((each) => each.field));
      // A hidden Lower Threshold doesn't widen the signature's scale away from the load scale.
      const scalesFor = (ltpOn: boolean) => {
        const mainRange = extentOf(panel.lines.filter((line) => line.id !== 'ltp' || ltpOn).map((line) => line.field));
        const colinear = mainRange && loadRange ? colinearScales(loadRange, mainRange, irParams?.[panel.id === 'tp' ? 'ftp' : panel.id], chartDays, panel.lines[0].field, SIGNATURE_LOAD_FIELDS[panel.id].tl) : null;
        return { mainRange, colinear };
      };
      const { colinear } = scalesFor(on.ltp);
      // Its min/max are set by fitScales below, once the load scale exists.
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
          strictMinMax: colinear != null,
          extraMin: colinear ? 0 : 0.05,
          extraMax: colinear ? 0 : 0.05,
          minWidth: 44,
        })
      );

      // The chart's own name, over the top-left of its plot.
      chart.plotContainer.children.push(
        am5.Label.new(root, { text: panel.title, position: 'absolute', x: 8, y: 4, fontSize: 12, fontWeight: '600', fill: am5.color('#374151') })
      );

      const addLine = (label: string, field: string, color: string, width: number, dash: number | undefined, axis: any, styled = false) => {
        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: label,
            xAxis: dateAxis,
            yAxis: axis,
            valueXField: 't',
            valueYField: field,
            stroke: am5.color(color),
            fill: am5.color(color),
          })
        );
        series.strokes.template.setAll({
          strokeWidth: width,
          strokeDasharray: dash ? [dash, dash] : undefined,
          strokeOpacity: styled ? 0.6 : 0.95,
          ...(styled ? { templateField: 'formStyle' } : {}),
        });
        series.data.setAll(chartDays);
        return series;
      };

      for (const line of panel.lines) {
        const series = addLine(line.label, line.field, line.color, line.width, line.dash, yAxis);
        if (SIGNATURE_TOGGLES.some((toggle) => toggle.id === line.id)) track(line.id, series);
      }

      // Loads, form, focus and specificity: each kind of thing on a right-hand scale of its own (training and
      // recovery load share one), which only exists while something on it is showing.
      const scales: Record<string, { axis: any; ids: string[] }> = {};
      for (const overlay of panelOverlays) {
        let scale = scales[overlay.axis];
        if (!scale) {
          const renderer = am5xy.AxisRendererY.new(root, { opposite: true });
          renderer.grid.template.set('forceHidden', true);
          renderer.labels.template.setAll({ ...labelStyle, fill: am5.color(overlay.color) });
          // Set to what everything on this scale reaches (and to zero where it can't go below it).
          const range = extentOf(panelOverlays.filter((each) => each.axis === overlay.axis).map((each) => each.field));
          const axis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
              renderer,
              strictMinMax: overlay.max != null || (overlay.axis === 'load' && colinear != null),
              minWidth: 36,
              ...(overlay.axis === 'load' && colinear
                ? { extraMin: 0, extraMax: 0 }
                : overlay.max != null
                ? { min: overlay.axisMin ?? 0, max: overlay.max }
                : range
                  ? {
                      min: overlay.axisMin ?? range.min,
                      max: range.max,
                      extraMax: 0.05,
                      ...(overlay.axisMin == null ? { extraMin: 0.05 } : {}),
                    }
                  : { min: overlay.axisMin }),
            })
          );
          scale = scales[overlay.axis] = { axis, ids: [] };
          overlayAxes.push(scale);
        }
        scale.ids.push(overlay.id);
        track(overlay.id, addLine(overlay.label, overlay.field, overlay.color, 2, overlay.dash, scale.axis, overlay.styled));
      }

      // Fits the signature's scale (and the load scale lined up with it) to the lines showing.
      const loadAxis = scales.load?.axis;
      const fitScales = () => {
        const now = scalesFor(on.ltp);
        const sig = now.colinear?.sig ?? now.mainRange;
        if (sig) yAxis.setAll({ min: sig.min, max: sig.max });
        if (now.colinear && loadAxis) loadAxis.setAll({ min: now.colinear.load.min, max: now.colinear.load.max });
      };
      fitScales();
      if (panel.lines.some((line) => line.id === 'ltp')) ltpRescales.push(fitScales);

      const eventsAxis = addEventsAxis(root, chart);

      // One balloon lists every line showing, on all the charts, wherever the pointer is: the day's whole signature
      // at a glance. The cursors follow each other across the charts, but only the chart under the pointer
      // shows it, since three boxes at once would sit on top of one another.
      const carrier = addDayTooltip(
        root,
        chart,
        dateAxis,
        eventsAxis,
        chartDays,
        () => [],
        selected,
        {},
        balloonHtml
      );
      const tooltip = carrier.get('tooltip');
      tooltip.set('forceHidden', true);
      chart.plotContainer.events.on('pointerover', () => tooltip.set('forceHidden', false));
      chart.plotContainer.events.on('pointerout', () => tooltip.set('forceHidden', true));
      // Guide labels run down from the top of their line, so they go on the top chart only, starting below its title.
      addTodayGuide(dateAxis, chartDays, today, index === 0, TITLE_INSET);
      addProgramStartGuide(dateAxis, chartDays, programStart, index === 0, TITLE_INSET);

      // A faint guideline down the chart for each marker, so what it did to the lines can be followed from the strip.
      if (markerStrip) {
        activityMarkers.forEach((marker) => {
          const kind = MARKER_KIND_ORDER.find((each) => marker.kinds.includes(each))!;
          const range = dateAxis.createAxisRange(dateAxis.makeDataItem({ value: marker.t }));
          range.get('grid').setAll({
            stroke: am5.color(MARKER_KINDS[kind].color),
            strokeOpacity: activityMarkers.length > GUIDELINES_ALWAYS_MAX ? 0 : GUIDE_OPACITY,
            strokeWidth: 1,
            strokeDasharray: [2, 3],
            visible: true,
          });
          const existing = guides.find((guide) => guide.marker.index === marker.index);
          if (existing) existing.ranges.push(range);
          else guides.push({ marker, ranges: [range] });
        });
      }
    });

    // The strips: their plots lined up with the charts' (whatever width the right-hand scales take), and their
    // cursors joined to the charts'.
    if (breakthroughStrip) {
      addBreakthroughDiamonds(root, breakthroughStrip.chart, breakthroughStrip.dateAxis, chartMarks);
      addTodayGuide(breakthroughStrip.dateAxis, chartDays, today, false);
      addProgramStartGuide(breakthroughStrip.dateAxis, chartDays, programStart, false);
    }
    if (markerStrip) {
      const lanes = addMarkerLanes(root, markerStrip.chart, markerStrip.dateAxis, activityMarkers, highlightGuide);
      for (const kind of MARKER_KIND_ORDER) {
        if (lanes[kind]) track(markerToggle(kind), lanes[kind]);
      }
      addTodayGuide(markerStrip.dateAxis, chartDays, today, false);
      addProgramStartGuide(markerStrip.dateAxis, chartDays, programStart, false);
    }
    if (strips.length) {
      const align = () => {
        const bounds = charts[0].plotContainer.globalBounds();
        const left = Math.max(0, Math.round(bounds.left));
        const right = Math.max(0, Math.round(root.width() - bounds.right));
        for (const { chart: each } of strips) {
          if (each.get('paddingLeft') !== left || each.get('paddingRight') !== right) each.setAll({ paddingLeft: left, paddingRight: right });
        }
      };
      charts[0].plotContainer.events.on('boundschanged', align);
      let settling = 12;
      root.events.on('frameended', () => {
        if (settling-- > 0) align();
      });
      for (const { chart: each, dateAxis: axis } of strips) addSelectionCursor(root, each, axis, selected);
    }

    const stripCharts = strips.map((each) => each.chart);
    const cursors = [...charts, ...stripCharts].map((each) => each.get('cursor'));
    cursors.forEach((cursor, i) => cursor.set('syncWith', cursors.filter((_, j) => j !== i)));

    applyToggles();
    [...charts, ...stripCharts].forEach((each) => each.appear(300, 50));
  }

  /** Bring out the guideline of the marker the pointer is on (or put them all back). */
  function highlightGuide(index: number | null) {
    for (const guide of guides) {
      const visible = guideVisible(guide.marker);
      const base = guides.length > GUIDELINES_ALWAYS_MAX ? 0 : GUIDE_OPACITY;
      for (const range of guide.ranges) {
        range.get('grid').setAll({ strokeOpacity: !visible ? 0 : guide.marker.index === index ? 0.75 : base });
      }
    }
  }

  const guideVisible = (marker: ChartMarker) => marker.kinds.some((kind) => on[markerToggle(kind)]);

  /** Every toggle is applied to all the charts; a right-hand scale exists only while something on it is showing. */
  function applyToggles() {
    for (const [id, list] of Object.entries(toggleSeries)) {
      for (const series of list) (on[id] ? series.show(0) : series.hide(0));
    }
    for (const scale of overlayAxes) {
      scale.axis.set('visible', scale.ids.some((id) => on[id]));
    }
    highlightGuide(null);
  }

  function toggle(id: string) {
    on = { ...on, [id]: !on[id] };
    saveStorage('progressionCharts', 'signatureLines', on);
    // The breakthrough strip comes and goes with what the two chips leave in it, which the change of `marks` redraws.
    if (id !== 'breakthroughs' && id !== 'raceEvents') applyToggles();
    if (id === 'ltp') for (const rescale of ltpRescales) rescale();
  }

  $: toggles = [
    ...SIGNATURE_TOGGLES.filter((entry) => entry.id !== 'breakthroughs' || hasBreakthroughs).map((entry) => ({
      id: entry.id,
      label: entry.label,
      color: entry.color,
      active: on[entry.id],
    })),
    ...(hasRaceEvents ? [{ id: 'raceEvents', label: 'Race / Event', color: RACE_EVENT_COLORS.Race, color2: RACE_EVENT_COLORS.Event, active: on.raceEvents }] : []),
    ...markerKinds.map((kind) => ({
      id: markerToggle(kind),
      label: MARKER_KINDS[kind].label,
      color: MARKER_KINDS[kind].color,
      active: on[markerToggle(kind)],
    })),
  ];

  onMount(() => {
    mounted = true;
  });
  onDestroy(() => {
    root?.dispose();
    root = null;
  });
</script>

<div class="flex flex-col gap-2">
  <div class="relative w-full h-[540px] md:h-[660px]">
    <div
      bind:this={chartEl}
      role="img"
      aria-label="Fitness signature charts: threshold power, high intensity energy and peak power, with any flagged or manually set activities above them. Drag across them to choose the dates; the date range can also be set in the filters."
      class="absolute inset-0"
    ></div>
    {#if !days.length}
      <div class="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
        No fitness history in this date range.
      </div>
    {/if}
  </div>

  <div class="flex flex-row flex-wrap gap-2" role="group" aria-label="Chart lines">
    {#each toggles as entry (entry.id)}
      <button
        type="button"
        class={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-solid px-2.5 py-1 text-xs cursor-pointer bg-white transition-opacity',
          entry.active ? 'border-border text-foreground' : 'border-border text-muted-foreground opacity-60'
        )}
        aria-pressed={entry.active}
        on:click={() => toggle(entry.id)}
      >
        <span class="inline-block h-2.5 w-2.5 rounded-full" style="background: {entry.active ? ('color2' in entry ? `linear-gradient(135deg, ${entry.color} 50%, ${entry.color2} 50%)` : entry.color) : 'transparent'}; border: 1.5px solid {entry.color}"></span>
        {entry.label}
      </button>
    {/each}
  </div>
</div>

<style>
  :global(.am5-layer) {
    font-family: inherit;
  }
</style>
